import type { IAddressProvider } from "@/domains/address/providers/address-provider.ts";
import type { ECountry } from "@/domains/country/country.types.ts";

import { GoogleMapsAddressSearchQueryKeys } from "@/api/google-maps/search/google-maps-address-search-query-keys.ts";
import { GouvAddressSearchQueryKeys } from "@/api/gouv/search/gouv-address-search-query-keys.ts";
import { EAddressProvider } from "@/domains/address/providers/address-provider.types.ts";
import { FrenchGovernmentAddressProvider } from "@/domains/address/providers/french-government/french-government-address-provider.ts";
import { GoogleAddressProvider } from "@/domains/address/providers/google/google-address-provider.ts";
import { assert } from "@/lib/utils.ts";

export interface IAddressProviderVisitor<OUTPUT = unknown> {
  visitFrenchGovernment: () => OUTPUT;
  visitGoogle: () => OUTPUT;
}

export type TAddressProviderVisitorOutput<V> = V extends IAddressProviderVisitor<infer R> ? R : never;

const addressProviderVisitorMap: Record<
  EAddressProvider,
  <K extends IAddressProviderVisitor>(visitor: K) => TAddressProviderVisitorOutput<K>
> = {
  [EAddressProvider.GOOGLE]: <K extends IAddressProviderVisitor>(visitor: K) =>
    visitor.visitGoogle() as TAddressProviderVisitorOutput<K>,
  [EAddressProvider.FRENCH_GOVERNMENT]: <K extends IAddressProviderVisitor>(visitor: K) =>
    visitor.visitFrenchGovernment() as TAddressProviderVisitorOutput<K>,
};

export function acceptAddressProviderVisitor<K extends IAddressProviderVisitor>(
  provider: EAddressProvider,
  visitor: K,
): TAddressProviderVisitorOutput<K> {
  const visitFn = addressProviderVisitorMap[provider];
  assert(visitFn !== undefined, `No visitor function defined for address provider: ${provider}`);

  return visitFn(visitor);
}

export const FindAddressProviderVisitor: IAddressProviderVisitor<IAddressProvider> = {
  visitGoogle() {
    return GoogleAddressProvider;
  },
  visitFrenchGovernment() {
    return FrenchGovernmentAddressProvider;
  },
};

export const AutocompleteQueryKeyVisitor: IAddressProviderVisitor<
  (query: string, country: ECountry) => Readonly<Array<unknown>>
> = {
  visitGoogle() {
    return (query: string, country: ECountry) => {
      return GoogleMapsAddressSearchQueryKeys.autocomplete(query, country);
    };
  },
  visitFrenchGovernment() {
    return (query: string) => {
      return GouvAddressSearchQueryKeys.autocomplete(query);
    };
  },
};
